/** @file This file contains the component rendering the announcement button for the sidebar. */
import * as moment from 'moment';
import * as tsx from 'vue-tsx-support';
import * as stateMachine from '@codegrade/state-machine';
import { eitherAsync } from '@codegrade/utils';
import { UIPrefsStore, UserStore } from '@/store';
import { emptyVNode, loadBackgroundApis } from '@/utils';
import './style.less';
const PREF_NAME = 'last-read-announcement';
const states = stateMachine.Definition.create()
    .addState('not-loaded')
    .addState('loaded')
    .addState('error');
/** Main component handling the different states for the announcement center. */
export const Announcements = tsx.component({
    name: 'announcement',
    /**
     * @returns The unique identifier to be used in html ids and the index of
     * the last read announcement.
     */
    data() {
        return {
            state: states.run('not-loaded', {}),
        };
    },
    watch: {
        userId: {
            immediate: true,
            /** Loads the UI preferences as soon as the UserId changes. */
            handler() {
                if (this.userId !== 0) {
                    loadBackgroundApis(eitherAsync
                        .sleep(moment.duration(500, 'milliseconds'))
                        .chain(() => UIPrefsStore.loadUIPreference({ preference: PREF_NAME })));
                }
            },
        },
    },
    computed: {
        /** @returns The User id present in the store. */
        userId() {
            return UserStore.id();
        },
    },
    methods: {
        /** @returns The list of announcement that the user may see. */
        getViewableAnnouncements(announcements) {
            return Object.freeze(announcements
                .filter(ann => ann.canSee(this.$getRootInstance().$getNow))
                .sort((a, b) => b.compareTo(a)));
        },
        /**
         * Saves the chosen announcement id in the UI prefs as the last one read.
         *
         * @param announcementId The id of the latest read announcement.
         */
        saveLastRead(announcementId) {
            loadBackgroundApis(UIPrefsStore.patchUIPreference({
                preference: PREF_NAME,
                value: announcementId.value,
            }));
        },
        /**
         * Renders the Announcements component rendered state.
         *
         * @param data The data relative to the loaded state.
         * @returns The rendered content for the announcements component once
         * the data has been loaded.
         */
        renderLoadedNode(data) {
            return (<div class="announcements">
                    <data.implementation.Inner.LoadedAnnouncements announcements={this.getViewableAnnouncements(data.loadedAnnouncements)} lastRead={UIPrefsStore.getUIPref()(PREF_NAME, data.implementation.AnnouncementUtils.AnnouncementId.parser)} onClose={this.saveLastRead}/>
                </div>);
        },
        /**
         * Loads the announcement to be shown in the popover. Transitions to error state if
         * announcement could not be loaded.
         */
        loadData(token) {
            eitherAsync
                .sleep(moment.duration(500, 'milliseconds'))
                .chain(() => eitherAsync.all([
                eitherAsync.importToEitherAsync(import('./announcements'), 'announcements.ts'),
                eitherAsync.importToEitherAsync(import('./implementation'), 'implementation.tsx'),
            ]))
                .caseOf({
                Right: ([announcements, imp]) => {
                    this.state.transitionTo(token, 'loaded', {
                        loadedAnnouncements: announcements.announcementList.map(imp.AnnouncementUtils.Announcement.fromObject),
                        implementation: imp,
                    });
                },
                Left: error => {
                    this.state.forceTransitionTo('error', { error });
                },
            });
        },
    },
    /** @returns The node that should be rendered within the app sidebar.  */
    render() {
        return this.state.match({
            'not-loaded': (_, { token }) => {
                this.loadData(token);
                return emptyVNode();
            },
            loaded: data => this.renderLoadedNode(data),
            error: emptyVNode,
        });
    },
});
